import { useMutation, useQueryClient } from "@tanstack/react-query";
import { FlagIcon } from "lucide-react";
import { useState } from "react";
import { Heading } from "react-aria-components";

import { Button } from "@/components/base/button";
import { useAppForm } from "@/components/base/form";
import { Modal } from "@/components/base/modal";
import { title } from "@/components/base/primitives";
import { useTRPC } from "@/integrations/trpc/react";

export type AbandonRefFormProps = {
  tournamentDivisionId: number;
  teamId: number;
  poolMatchId?: number;
  playoffMatchId?: number;
  isDemo?: boolean;
};

export function AbandonRefForm(props: AbandonRefFormProps) {
  const [open, setOpen] = useState(false);

  return (
    <>
      <Button
        variant="text"
        size="sm"
        color="primary"
        onPress={() => setOpen(true)}
        tooltip="Abandoned Ref"
      >
        <FlagIcon size={16} />
      </Button>
      {open && (
        <AbandonRefFormContent
          {...props}
          isOpen={open}
          onOpenChange={setOpen}
        />
      )}
    </>
  );
}

function AbandonRefFormContent({
  teamId,
  poolMatchId,
  playoffMatchId,
  isDemo,
  isOpen,
  onOpenChange,
}: AbandonRefFormProps & {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const queryClient = useQueryClient();
  const trpc = useTRPC();

  const { mutate, failureReason } = useMutation({
    ...trpc.tournaments.abandonRef.mutationOptions(),
    onSuccess: () => {
      queryClient.invalidateQueries(
        trpc.matches.get.queryOptions({ poolMatchId, playoffMatchId }),
      );
      queryClient.invalidateQueries(trpc.tournaments.pathFilter());

      onOpenChange(false);
    },
  });

  const form = useAppForm({
    defaultValues: {},
    onSubmit: () => {
      mutate({
        poolMatchId,
        playoffMatchId,
        teamId,
      });
    },
  });

  return (
    <Modal isOpen={isOpen} onOpenChange={onOpenChange}>
      <div className="relative flex flex-col space-y-4 p-3">
        <Heading className={title({ size: "sm" })} slot="title">
          Abandon Ref
        </Heading>

        <div>
          <p className="mb-2 text-sm text-gray-700">
            Are you sure you want to mark this team as having abandoned ref
            duties?
          </p>

          <p className="mb-2 text-sm text-gray-700">
            You can undo this action from the team's table.
          </p>

          <p className="text-sm text-gray-700">
            <span>
              Both players on this team will have their memberships revoked.
            </span>

            {isDemo && (
              <span className='before:content-["*"]'>
                Nothing happens in demo tournaments.
              </span>
            )}
          </p>
        </div>

        <form
          className="flex flex-col space-y-6"
          onSubmit={(e) => {
            e.preventDefault();

            form.handleSubmit();
          }}
        >
          {failureReason && (
            <form.AppForm>
              <form.Alert
                title={"Unable to mark abandoned ref"}
                description={failureReason.message}
              />
            </form.AppForm>
          )}

          <form.AppForm>
            <form.Footer>
              <Button onPress={() => onOpenChange(false)}>Cancel</Button>

              <form.SubmitButton requireChange={false}>
                Confirm
              </form.SubmitButton>
            </form.Footer>
          </form.AppForm>
        </form>
      </div>
    </Modal>
  );
}
